import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Action } from '~/lib/ApiModel';
import { hasTerminated, requiresUserAttention } from '~/lib/helper/ActionHelper';
import { orderByLookup } from '~/lib/helper/ArrayHelper';
import UnmountHelper from '~/lib/helper/UnmountHelper';
import { ActionStore } from '~/lib/store/ActionStore';
import { notifyActionFinished } from './ActionNotice';
import ActionProgressSection from './ActionProgressMini';
import TrackedActionModal from './TrackedAction';

type ActionUIProps = {
    store: ActionStore;
    trackedActionId?: number;
    hideUI?: boolean;
    // An action whose progress another owner already displays (the client-driven upload banner), so
    // its plain progress card is withheld from the shared dock. It still surfaces when it needs the
    // user (interaction required / blocking invoice), since that card carries a control.
    suppressedActionId?: number;
};

/*
    Some of our server operations spawn multiple actions that may run simultaneously (eg create_server, update_network)
    We currently only have room to display 1. We deprioritize certain utility actions so that the main action is displayed.
*/
const deprioritizedActionTypes = ['update_network'];

function deprioritizeUtilityActions(actionList: Action[]) {
    return orderByLookup(actionList, deprioritizedActionTypes, (a) => a.type, 'desc');
}

@observer
export default class ActionUI extends React.Component<ActionUIProps> {
    private readonly store = this.props.store;

    @observable
    maximizedActionId?: number = undefined;

    // Mirror of props.trackedActionId: props isn't observable, so the computed below
    // reads this local observable instead (synced in componentDidUpdate).
    @observable
    private trackedActionId = this.props.trackedActionId;

    @observable
    private suppressedActionId = this.props.suppressedActionId;

    mountManager = new UnmountHelper(this);

    private readonly bannerControlRef = React.createRef<HTMLDivElement>();

    // The action whose modal the user just closed. Its banner takes focus only if it is the one shown
    // next: currentAction changing under a maximised modal, or closing a finished action while another
    // is pending, also swaps in a banner but must not pull focus away from wherever the user is.
    private focusBannerForActionId?: number = undefined;

    // A maximised action is never suppressed: the user opened that modal and only they close it.
    private isSuppressed(action: Action) {
        return (
            action.id === this.suppressedActionId &&
            action.id !== this.maximizedActionId &&
            !requiresUserAttention(action)
        );
    }

    @computed
    get currentAction(): Action | undefined {
        // we may have more than one inProgress or Queued action, in which case we ensure it is the most important one that is shown
        // this prevents us showing the "update_network" action when there is a "create_server" action also running
        const actionsPending = deprioritizeUtilityActions(
            this.store.actionsPending.filter((a) => !this.isSuppressed(a))
        );
        const activeTrackedAction =
            actionsPending.find((a) => a.id === this.trackedActionId) ??
            this.store.actionsFinished.find((a) => a.id === this.maximizedActionId);
        const actionMonitored = activeTrackedAction || (actionsPending.length > 0 ? actionsPending[0] : undefined);
        return actionMonitored;
    }

    constructor(props: ActionUIProps) {
        super(props);
        makeObservable(this);
    }

    @action
    maximize(actionId: number) {
        this.maximizedActionId = actionId;
    }

    @action
    minimize() {
        this.focusBannerForActionId = this.maximizedActionId;
        this.maximizedActionId = undefined;
    }

    componentDidMount() {
        const store = this.store;
        this.mountManager.onUnmount(store.onActionComplete(notifyActionFinished));
        store.startRefreshingActions();
        this.mountManager.onUnmount(() => store.stopRefreshingActions());
    }

    componentDidUpdate(oldProps: ActionUIProps) {
        // On minimise, move focus to the banner's restore control, which reactstrap's own focus return
        // (to <body> or the page control that opened the modal) would miss. Restoring needs no handling —
        // reactstrap focuses the modal when it re-mounts.
        if (this.focusBannerForActionId !== undefined) {
            if (this.currentAction?.id === this.focusBannerForActionId) {
                this.bannerControlRef.current?.focus();
            }
            this.focusBannerForActionId = undefined;
        }
        if (this.props.suppressedActionId !== oldProps.suppressedActionId) {
            runInAction(() => {
                this.suppressedActionId = this.props.suppressedActionId;
            });
        }
        const trackedActionId = this.props.trackedActionId;
        if (trackedActionId !== oldProps.trackedActionId) {
            runInAction(() => {
                this.trackedActionId = trackedActionId;
            });
            //when we track a new action, we maximize
            if (trackedActionId) {
                this.maximize(trackedActionId);
            }
        }
    }

    render() {
        const currentAction = this.currentAction;
        if (this.props.hideUI || !currentAction) return null;

        if (this.maximizedActionId === currentAction.id) {
            return (
                <TrackedActionModal
                    action={currentAction}
                    onClose={() => this.minimize()}
                    abort={(a) => this.store.abortAction(a)}
                    proceed={(a) => this.store.proceedWithAction(a)}
                />
            );
        } else if (!hasTerminated(currentAction)) {
            return (
                <ActionProgressSection
                    action={currentAction}
                    onClick={() => this.maximize(currentAction.id)}
                    controlRef={this.bannerControlRef}
                ></ActionProgressSection>
            );
        } else {
            return null;
        }
    }
}
